import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { useParams } from "react-router-dom";
import { BASE_URL } from "../../../config/config";
import {
  noticeDaysFor,
  earliestMoveOutDate,
  DEFAULT_NOTICE_DAYS,
} from "../../../utils/noticePeriod";
import Wrapper from "../../../components/TenantWrapper";
import { useSelector } from "react-redux";
import TenantViolationPanel from "./TenantViolationPanel";
import TerminationCostPanel from "./TerminationCostPanel";

const UnregisterTenantRequestPage = () => {
  const open = useSelector((state) => state.sidebar.open);
  const getId = window.location.pathname.replace(
    "/unregister-tenant-request/",
    ""
  );
  const [landlordId, flatId] = getId.split("/");
  const [openDialog, setOpenDialog] = useState(false);
  const [dueDate, setDueDate] = useState("");
  const [reason, setReason] = useState("");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [responseNote, setResponseNote] = useState("");
  const [activeRequestId, setActiveRequestId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  const [cancelLoading, setCancelLoading] = useState(null);

  const [rejectLoading, setRejectLoading] = useState(null);

  // Notice period — how many days ahead the tenant must give notice.
  //
  // This is the same number the server validates. Previously the form
  // accepted any date (even tomorrow), and the owner would get a move-out
  // message without proper notice. Now the date picker itself won't allow a
  // date before the notice period.
  const [noticeDays, setNoticeDays] = useState(DEFAULT_NOTICE_DAYS);
  const earliestMoveOut = earliestMoveOutDate(noticeDays);

  const token =
    localStorage.getItem("token") || sessionStorage.getItem("ots_token");
  const userId = localStorage.getItem("userId"); // current user id

  // Fetch all requests for this tenant
  const fetchRequests = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${BASE_URL}/unregister-requests/${landlordId}/${flatId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRequests(data.requests);
    } catch (err) {
      console.error(err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  // Notice period comes from the property settings/agreement — the same
  // endpoint the termination cost panel also uses.
  useEffect(() => {
    if (!flatId) return;

    axios
      .get(`${BASE_URL}/termination-preview/${flatId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => {
        const fromRules = Number(data?.preview?.notice?.required) || 0;
        setNoticeDays(
          fromRules > 0
            ? fromRules
            : noticeDaysFor({ flat: data?.flat, initiatedBy: "TENANT" })
        );
      })
      .catch(() => setNoticeDays(DEFAULT_NOTICE_DAYS));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatId]);

  // Have the date already correct as soon as the dialog opens.
  useEffect(() => {
    if (openDialog && (!dueDate || dueDate < earliestMoveOut)) {
      setDueDate(earliestMoveOut);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDialog, earliestMoveOut]);

  // Handle create unregister
  const handleUnregister = async () => {
    if (!dueDate || !reason) {
      toast.error("Both move-out date and reason are required");
      return;
    }

    if (dueDate < earliestMoveOut) {
      toast.error(
        `The owner must be given ${noticeDays} days' notice — the earliest possible date is ${earliestMoveOut}.`
      );
      return;
    }

    setSubmitLoading(true);

    try {
      const { data } = await axios.post(
        `${BASE_URL}/unregister-tenant/${landlordId}/${flatId}`,
        {
          dueDate,
          reason,
          action: "CREATE",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success(data?.message || "Notice sent to owner");

      setOpenDialog(false);
      setReason("");

      fetchRequests();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to send request"
      );
      console.error(err.response?.data || err.message);
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle cancel request
  const handleCancel = async (requestId) => {
    setCancelLoading(requestId);

    try {
      await axios.post(
        `${BASE_URL}/unregister-tenant/${landlordId}/${flatId}`,
        {
          action: "CANCEL",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      fetchRequests();
    } catch (err) {
      console.error(err.response?.data || err.message);
    } finally {
      setCancelLoading(null);
    }
  };

  const handleReject = async (requestId) => {
    if (!responseNote) {
      alert("Please enter a reason for rejection");
      return;
    }

    setRejectLoading(requestId);

    try {
      await axios.post(
        `${BASE_URL}/unregister-tenant/${landlordId}/${flatId}`,
        {
          action: "REJECT",
          responseNote,
          requestId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setResponseNote("");
      setActiveRequestId(null);

      fetchRequests();
    } catch (err) {
      const data = err.response?.data;
      // A violation-backed move-out can't be rejected — the server explains
      // that the route is to respond on the violation instead. Show it rather
      // than swallowing it into the console.
      if (data?.message) alert(data.message);
      console.error(data || err.message);
    } finally {
      setRejectLoading(null);
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <Wrapper open={open} />
      <Box
        className={`main-container ${open ? "sidebar-open" : "sidebar-closed"}`}
      >
        <Typography variant="h5" mb={2}>
          Flat Unregister Requests
        </Typography>

        {/* Anything logged against this tenant, with the apology route. Sits
            above the move-out controls because an open violation is usually
            *why* the tenant is on this screen. */}
        <TenantViolationPanel tenantId={userId} />

        {/* What leaving early actually costs — shown before the tenant picks a
            date, not after they've committed to one. */}
        <TerminationCostPanel flatId={flatId} moveOutDate={dueDate} />

        {/* Button to open dialog */}
        <Button
          variant="contained"
          color="error"
          onClick={() => setOpenDialog(true)}
          sx={{ mb: 2 }}
        >
          Create Unregister Request
        </Button>

        {/* Dialog */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 700, fontSize: "1.2rem" }}>
            Create Unregister Request
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" mb={3}>
              The owner must be told at least <strong>{noticeDays} days</strong>{" "}
              before vacating. Your rent and bills will continue as normal
              until then.
            </Typography>
            <TextField
              label="Move-out date"
              type="date"
              fullWidth
              required
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: earliestMoveOut }}
              helperText={`Earliest possible date: ${earliestMoveOut}`}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Reason"
              multiline
              rows={4}
              fullWidth
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleUnregister}
              disabled={!dueDate || !reason || submitLoading}
              sx={{ minWidth: 150 }}
            >
              {submitLoading ? (
                <>
                  <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                  Sending...
                </>
              ) : (
                "Send Request"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Requests Table */}
        <Box mt={4}>
          {loading ? (
            <CircularProgress />
          ) : (
            <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Requested By</TableCell>
                  <TableCell>Effective Date</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Requested At</TableCell>
                  <TableCell>Action</TableCell> {/* New column */}
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((req) => (
                  <TableRow key={req._id}>
                    <TableCell>{req.status}</TableCell>
                    <TableCell>{req.requestedBy}</TableCell>
                    <TableCell>
                      {new Date(req.effectiveDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{req.reason}</TableCell>
                    <TableCell>
                      {new Date(req.requestedAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {req.status === "PENDING" ? (
                        req.requestedBy?.toUpperCase() === "TENANT" ? (
                          // If Tenant sent, show Cancel button
                          <Button
                            color="warning"
                            variant="contained"
                            onClick={() => handleCancel(req._id)}
                            disabled={cancelLoading === req._id}
                            sx={{ minWidth: 100 }}
                          >
                            {cancelLoading === req._id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              "Cancel"
                            )}
                          </Button>
                        ) : // If Landlord sent, show Reject button + input for reason
                        activeRequestId === req._id ? (
                          <Box>
                            <TextField
                              label="Reason"
                              value={responseNote}
                              onChange={(e) => setResponseNote(e.target.value)}
                              size="small"
                              sx={{ mb: 1 }}
                            />
                            <Button
                              color="error"
                              variant="contained"
                              onClick={() => handleReject(req._id)}
                              disabled={rejectLoading === req._id}
                              sx={{ minWidth: 100 }}
                            >
                              {rejectLoading === req._id ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                "Reject"
                              )}
                            </Button>
                            <Button
                              variant="outlined"
                              sx={{ ml: 1 }}
                              onClick={() => setActiveRequestId(null)}
                            >
                              Cancel
                            </Button>
                          </Box>
                        ) : (
                          <Button
                            variant="contained"
                            onClick={() => setActiveRequestId(req._id)}
                          >
                            Reject
                          </Button>
                        )
                      ) : (
                        "-"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
    </>
  );
};

export default UnregisterTenantRequestPage;
